import { memo, useEffect, useRef, useState } from "react";
import { OverlayPanel } from "primereact/overlaypanel";
import StatusBadge from "../Shared/StatusBadge";
import PrioridadeBadge from "../Shared/PrioridadeBadge";
import TempoBadge, { calcularFaixa } from "../Shared/TempoBadge";
import PrazoBadge from "../Shared/PrazoBadge";
import TaskCardDetalhes from "./TaskCardDetalhes";
import { processoInfo, etapaLabel } from "../../../utils/solicitacoes/processoRegistry";
import { humanizarEtapa } from "../../../utils/solicitacoes/statusConfig";

// Dispositivos sem hover "de verdade" (touch) não abrem o popover no toque — em alguns navegadores
// mobile isso exigiria um segundo toque para o onClick do card funcionar. Calculado uma vez (não muda em
// runtime), então não precisa de listener/hook por card.
const SUPORTA_HOVER = typeof window !== "undefined" && !!window.matchMedia
    && window.matchMedia("(hover: hover) and (pointer: fine)").matches;

const ATRASO_ABRIR_MS = 1000;

/**
 * Card de tarefa/solicitação reutilizado em todo o módulo (Kanban da Central de Tarefas, Kanban de
 * Todas as Tarefas, lista mobile de Central de Tarefas) — uma mudança aqui reflete em todos os
 * lugares. Tamanho fixo e compacto; ao passar o mouse (desktop), os detalhes adicionais aparecem num
 * Popover ao lado do card, sem alterar o tamanho dele nem empurrar os demais cards da coluna.
 *
 * `autoDetalhe` (default true): quando o card busca sozinho o detalhe do processo (uso no
 * KanbanBoard, onde ninguém mais busca isso). Quem já tem o detalhe carregado (ex.: lista mobile de
 * Central de Tarefas, que já busca via useEnriquecidas) passa `detalhe` pronto e `autoDetalhe={false}`
 * para não duplicar a chamada.
 */
function KanbanCard({ solicitacao, onClick, detalhe: detalheExterno = null, autoDetalhe = true }) {
    const [detalheProprio, setDetalheProprio] = useState(null);
    const [carregandoDetalhe, setCarregandoDetalhe] = useState(false);
    const cardRef = useRef(null);
    const popoverRef = useRef(null);
    const timeoutAbrir = useRef(null);

    const processo = processoInfo(solicitacao.tipoProcesso);

    useEffect(() => {
        if (!autoDetalhe || !processo?.temDetalhe) return undefined;
        let cancelado = false;
        setCarregandoDetalhe(true);
        processo.service.buscarDetalhe(solicitacao.id)
            .then(res => { if (!cancelado) setDetalheProprio(res.data); })
            .catch(() => { if (!cancelado) setDetalheProprio(null); })
            .finally(() => { if (!cancelado) setCarregandoDetalhe(false); });
        return () => { cancelado = true; };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [solicitacao.id, solicitacao.tipoProcesso, autoDetalhe]);

    useEffect(() => () => clearTimeout(timeoutAbrir.current), []);

    const detalhe = autoDetalhe ? detalheProprio : detalheExterno;
    const carregando = autoDetalhe && carregandoDetalhe;

    const etapaLabelTxt = solicitacao.etapaAtual
        ? (etapaLabel(solicitacao.tipoProcesso, solicitacao.etapaAtual) || humanizarEtapa(solicitacao.etapaAtual))
        : "Encerrada";
    const responsavelTxt = solicitacao.responsavelAtualNome || solicitacao.grupoResponsavelAtualNome || null;
    const ehGrupo = !solicitacao.responsavelAtualNome && !!solicitacao.grupoResponsavelAtualNome;
    const resumo = detalhe?.motivo || detalhe?.descricao || null;

    const faixaTempo = calcularFaixa(solicitacao.dataAtualizacao);
    const emAndamento = solicitacao.status === "EM_ANDAMENTO" || solicitacao.status === "ABERTA";
    const atrasado = emAndamento && faixaTempo.critico;

    function aoEntrar() {
        if (!SUPORTA_HOVER) return;
        clearTimeout(timeoutAbrir.current);
        timeoutAbrir.current = setTimeout(() => popoverRef.current?.show(null, cardRef.current), ATRASO_ABRIR_MS);
    }

    function aoSair() {
        if (!SUPORTA_HOVER) return;
        clearTimeout(timeoutAbrir.current);
        popoverRef.current?.hide();
    }

    function abrirTarefa(e) {
        clearTimeout(timeoutAbrir.current);
        popoverRef.current?.hide();
        onClick?.(e);
    }

    const bordaDestaque = atrasado
        ? "border-l-[3px] border-l-destructive border-y-border border-r-border hover:border-primary/30"
        : "border-border hover:border-primary/30";

    return (
        <div
            ref={cardRef}
            onClick={abrirTarefa}
            onMouseEnter={aoEntrar}
            onMouseLeave={aoSair}
            className={`bg-card border rounded-lg shadow-card p-3 cursor-pointer transition-all duration-150 hover:shadow-soft hover:-translate-y-0.5 ${bordaDestaque}`}
        >
            <div className="flex items-start justify-between gap-2">
                <span className="text-[10px] uppercase tracking-wide text-muted-foreground font-semibold flex items-center gap-1.5 min-w-0">
                    {processo?.icone && <i className={`${processo.icone} shrink-0`} />}
                    <span className="truncate">{processo?.label || solicitacao.tipoProcesso}</span>
                </span>
                {detalhe?.prioridade && <PrioridadeBadge prioridade={detalhe.prioridade} />}
            </div>

            <div className="flex items-center justify-between gap-2 mt-1 mb-1.5">
                <span className="font-display text-base font-bold text-foreground truncate">{solicitacao.numero}</span>
                <StatusBadge status={solicitacao.status} />
            </div>

            {resumo && <p className="text-xs text-muted-foreground mb-2 line-clamp-1" title={resumo}>{resumo}</p>}

            <div className="flex flex-col gap-1 mb-2 text-xs text-foreground/80">
                <span className="truncate" title={etapaLabelTxt}>
                    <i className="fa fa-flag mr-1.5 text-muted-foreground w-3 text-center" />
                    {etapaLabelTxt}
                </span>
                <span className="truncate text-muted-foreground" title={responsavelTxt || "-"}>
                    <i className={`fa ${ehGrupo ? "fa-users" : "fa-user"} mr-1.5 w-3 text-center`} />
                    {responsavelTxt || "-"}
                </span>
                {carregando ? (
                    <div className="h-3.5 w-24 bg-muted rounded animate-pulse mt-0.5" />
                ) : detalhe?.filialNome && (
                    <span className="truncate text-muted-foreground" title={detalhe.filialNome}>
                        <i className="fa fa-building mr-1.5 w-3 text-center" />
                        {detalhe.filialNome}
                    </span>
                )}
            </div>

            <div className="flex items-center justify-between gap-2 pt-1.5 border-t border-border">
                <div className="flex items-center gap-1.5 flex-wrap">
                    <TempoBadge data={solicitacao.dataAtualizacao} />
                    {solicitacao.prazoEtapa && <PrazoBadge prazo={solicitacao.prazoEtapa} />}
                </div>
                {atrasado && (
                    <span className="text-[10px] font-semibold text-destructive whitespace-nowrap" title="Muito tempo parada nesta etapa">
                        <i className="fa fa-triangle-exclamation mr-1" />
                        Atrasada há {faixaTempo.dias}d
                    </span>
                )}
            </div>

            {SUPORTA_HOVER && (
                <OverlayPanel ref={popoverRef} dismissable={false} className="pointer-events-none w-80 max-w-[90vw] shadow-soft">
                    <TaskCardDetalhes solicitacao={solicitacao} detalhe={detalhe} />
                </OverlayPanel>
            )}
        </div>
    );
}

export default memo(KanbanCard);
